import React from 'react';
import { 
  organizationSchema, 
  localBusinessSchema, 
  serviceSchemas, 
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateArticleSchema
} from '../data/schemas';

// Serialize JSON-LD safely for embedding inside <script>. Escapes
// characters that could break out of the script context or be
// interpreted by an HTML parser (</script>, <!--, etc.).
// Ref: https://html.spec.whatwg.org/multipage/scripting.html#restrictions-for-contents-of-script-elements
function safeJsonLd(schema) {
  try {
    return JSON.stringify(schema)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026')
      .replace(/\u2028/g, '\\u2028')
      .replace(/\u2029/g, '\\u2029');
  } catch {
    return '{}';
  }
}

// Inject JSON-LD schema into head
export function SchemaScript({ schema }) {
  return (
    <script 
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
}

// Global schemas for all pages
export function GlobalSchemas() {
  return (
    <>
      <SchemaScript schema={organizationSchema} />
      <SchemaScript schema={localBusinessSchema} />
      {serviceSchemas.map((schema, i) => (
        <SchemaScript key={schema['@id'] || schema.url || `service-${i}`} schema={schema} />
      ))}
    </>
  );
}

// Breadcrumb schema component
export function BreadcrumbSchema({ items }) {
  const schema = generateBreadcrumbSchema(items);
  return <SchemaScript schema={schema} />;
}

// FAQ schema component  
export function FAQSchema({ faqs }) {
  const schema = generateFAQSchema(faqs);
  return <SchemaScript schema={schema} />;
}

// Article schema component
export function ArticleSchema({ article }) {
  const schema = generateArticleSchema(article);
  return <SchemaScript schema={schema} />;
}

// Breadcrumb UI Component with Schema
export function Breadcrumbs({ items }) {
  const schemaItems = items.map((item, i) => ({
    name: item.label,
    url: `https://ihgind.com${item.href}`
  }));

  return (
    <>
      <BreadcrumbSchema items={schemaItems} />
      <nav aria-label="Breadcrumb" className="mb-6">
        <ol className="flex items-center gap-2 text-sm font-body">
          <li>
            <a href="/" className="text-concrete hover:text-obsidian transition-colors">
              Home
            </a>
          </li>
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="text-concrete">/</span>
              {i === items.length - 1 ? (
                <span className="text-obsidian font-medium">{item.label}</span>
              ) : (
                <a href={item.href} className="text-concrete hover:text-obsidian transition-colors">
                  {item.label}
                </a>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}

export default GlobalSchemas;
